import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent } from 'react';
import { catalogAttributes } from '@bb/contracts';
import { Button, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type AttributeDetails, type AttributeProducer, type AttributeStatus, type PiiClass } from '../api/client.ts';
import { usePermissions } from '../features/access/usePermissions.ts';
import { EditorShell } from '../features/editor/EditorShell.tsx';
import { useEditorState } from '../features/editor/useEditorState.ts';
import { useObjectTab } from '../features/tabs/objectTabs.ts';
import { ATTRIBUTE_STATUS_LABEL, PiiBadge } from './AttributesPage.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const SOURCE_LABEL: Record<AttributeDetails['piiClassificationSource'], string> = {
  default: 'default',
  known_key: 'known personal-data key',
  heuristic: 'name looks like personal data',
  manual: 'set by a person',
};

type Draft = { description: string; owner: string; producer: AttributeProducer };
const draftOf = (a: AttributeDetails): Draft => ({ description: a.description ?? '', owner: a.owner ?? '', producer: a.producer });

/** One attribute (BB-28): metadata with an unsaved-changes guard, status, PII class, conflicts, usage and top values. */
export function AttributePage() {
  const { environmentId, key } = useParams({ from: '/env/$environmentId/data/attributes/$key' });
  const { api } = useRouteContext({ from: '/env/$environmentId/data/attributes/$key' });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const queryKey = ['attribute', environmentId, key];
  const attribute = useQuery({ queryKey, queryFn: () => api.getAttribute(environmentId, key) });
  const server = useMemo(() => (attribute.data ? draftOf(attribute.data) : undefined), [attribute.data]);
  const editor = useEditorState(server);
  const draft = editor.draft;
  const dirty = editor.isDirty;
  useObjectTab(environmentId, attribute.data ? { type: 'attribute', id: key, title: key, url: `/env/${environmentId}/data/attributes/${encodeURIComponent(key)}`, listUrl: `/env/${environmentId}/data/attributes` } : null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: ['attributes', environmentId] });
  };
  const save = useMutation({
    mutationFn: (d: Draft) => api.updateAttribute(environmentId, key, { description: d.description.trim() || null, owner: d.owner.trim() || null, producer: d.producer }),
    onSuccess: async (_result, d) => {
      editor.markSaved({ description: d.description.trim(), owner: d.owner.trim(), producer: d.producer });
      await refresh();
    },
  });
  const status = useMutation({ mutationFn: (next: AttributeStatus) => api.updateAttribute(environmentId, key, { status: next }), onSuccess: refresh });

  if (attribute.isPending || !draft) return attribute.isError ? <ErrorState title="The attribute could not be loaded" message={errorText(attribute.error)} /> : <p aria-busy="true">Loading attribute…</p>;
  const a = attribute.data!;
  const standard = a.kind === 'standard';
  const canEdit = has('catalog.edit') && !standard;
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (dirty) save.mutate(draft);
  };

  return (
    <EditorShell dirty={dirty} onSave={() => save.mutateAsync(draft)} objectLabel="attribute">
    <section>
      <h1>{a.key}</h1>
      <p>
        <span className="tag tag--neutral">{ATTRIBUTE_STATUS_LABEL[a.status]}</span> <PiiBadge piiClass={a.piiClass} review={a.piiReviewRequired} /> {a.dataType} ·{' '}
        {a.lastSeenAt ? `last seen ${a.lastSeenAt.slice(0, 10)}` : 'not seen yet'}
      </p>
      {standard ? (
        <ReadOnlyBanner>Standard attributes are defined by Braze Brother and cannot be changed.</ReadOnlyBanner>
      ) : (
        !canEdit && <ReadOnlyBanner>Changing the registry needs the catalog.edit permission.</ReadOnlyBanner>
      )}

      <form aria-label="Attribute details" onSubmit={submit} className="stack-form">
        <label>
          Description
          <textarea value={draft.description} disabled={!canEdit} onChange={(ev) => editor.update({ description: ev.target.value })} />
        </label>
        <label>
          Owner
          <input value={draft.owner} disabled={!canEdit} onChange={(ev) => editor.update({ owner: ev.target.value })} />
        </label>
        <label>
          Producer
          <select value={draft.producer} disabled={!canEdit} onChange={(ev) => editor.update({ producer: ev.target.value as AttributeProducer })}>
            {catalogAttributes.AttributeProducer.options.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        {canEdit && (
          <Button type="submit" disabled={!dirty || save.isPending}>
            Save
          </Button>
        )}
        {dirty && <span className="hint">Unsaved changes</span>}
        {save.isError && <span role="alert">{errorText(save.error)}</span>}
      </form>

      {canEdit && (
        <div className="filters" role="group" aria-label="Change status">
          {catalogAttributes.ATTRIBUTE_STATUS_TRANSITIONS[a.status].map((next) => (
            <Button key={next} onClick={() => status.mutate(next)} disabled={status.isPending} variant={next === 'blocked' ? 'danger' : 'secondary'}>
              Mark {ATTRIBUTE_STATUS_LABEL[next].toLowerCase()}
            </Button>
          ))}
          {status.isError && <span role="alert">{errorText(status.error)}</span>}
        </div>
      )}
      {a.status === 'blocked' && <p className="hint">New values are refused on ingest; stored values stay until retention removes them.</p>}

      <h2>Personal data</h2>
      <p>
        {a.piiClass === 'pii' ? 'PII' : 'Not PII'} — {SOURCE_LABEL[a.piiClassificationSource]}.
        {a.piiClass === 'pii' && ' Values are hidden from people without the pii.view permission, in exports and in previews.'}
      </p>
      {!standard && <PiiClassForm key={`${a.piiClass}:${a.piiReviewRequired}`} attribute={a} canEdit={has('catalog.edit') && has('pii.view')} onSaved={refresh} />}

      <h2>Top values</h2>
      {a.topValues ? (
        a.topValues.length === 0 ? (
          <p className="hint">No profile holds a value yet.</p>
        ) : (
          <table className="table" aria-label="Top values">
            <thead>
              <tr>
                <th scope="col">Value</th>
                <th scope="col">Profiles</th>
              </tr>
            </thead>
            <tbody>
              {a.topValues.map((v) => (
                <tr key={JSON.stringify(v.value)}>
                  <td>
                    <code>{JSON.stringify(v.value)}</code>
                  </td>
                  <td>{v.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : (
        <p className="hint">{a.topValuesHidden === 'pii' ? 'Hidden: PII.' : 'Shown only for text and boolean attributes.'}</p>
      )}
      <p className="hint">
        {a.profilesWithValue.capped ? 'More than ' : ''}
        {a.profilesWithValue.count} profiles hold a value.
      </p>

      <h2>Type conflicts</h2>
      {a.conflicts.length === 0 ? (
        <p className="hint">No writes of another type.</p>
      ) : (
        <table className="table" aria-label="Type conflicts">
          <thead>
            <tr>
              <th scope="col">Received</th>
              <th scope="col">Source</th>
              <th scope="col">Count</th>
              <th scope="col">Last sample</th>
            </tr>
          </thead>
          <tbody>
            {a.conflicts.map((c) => (
              <tr key={`${c.receivedType}:${c.sourceKind}`}>
                <td>{c.receivedType}</td>
                <td>{c.sourceKind}</td>
                <td>{c.count}</td>
                <td>
                  <code>{c.sample ?? '—'}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Aliases</h2>
      {a.aliases?.length ? (
        <p>
          Also received as: {a.aliases.map((alias) => <code key={alias}>{alias} </code>)}
        </p>
      ) : (
        <p className="hint">No old spellings.</p>
      )}

      <h2>Used in</h2>
      {a.usage.length === 0 ? (
        <p className="hint">Nothing uses this attribute yet.</p>
      ) : (
        <ul>
          {a.usage.map((u) => (
            <li key={`${u.objectType}:${u.objectId}`}>
              {u.name} ({u.objectType})
            </li>
          ))}
        </ul>
      )}
    </section>
    </EditorShell>
  );
}

/** PII class change: needs pii.view; lowering to "not PII" asks for a reason that goes to the audit log. */
function PiiClassForm({ attribute, canEdit, onSaved }: { attribute: AttributeDetails; canEdit: boolean; onSaved: () => Promise<void> }) {
  const { environmentId } = useParams({ from: '/env/$environmentId/data/attributes/$key' });
  const { api } = useRouteContext({ from: '/env/$environmentId/data/attributes/$key' });
  const [piiClass, setPiiClass] = useState<PiiClass>(attribute.piiClass);
  const [reason, setReason] = useState('');
  const downgrade = attribute.piiClass === 'pii' && piiClass === 'none';
  const reasonShort = downgrade && reason.trim().length < catalogAttributes.PII_DOWNGRADE_REASON_MIN;
  const change = useMutation({
    mutationFn: () => api.updateAttribute(environmentId, attribute.key, { piiClass, ...(downgrade ? { reason: reason.trim() } : {}) }),
    onSuccess: onSaved,
  });
  if (!canEdit) return <p className="hint">Changing the PII class needs the catalog.edit and pii.view permissions.</p>;
  const unchanged = piiClass === attribute.piiClass && !attribute.piiReviewRequired;
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!unchanged && !reasonShort) change.mutate();
  };
  return (
    <form aria-label="PII class" className="stack-form" onSubmit={submit}>
      <label>
        Class
        <select value={piiClass} onChange={(ev) => setPiiClass(ev.target.value as PiiClass)}>
          <option value="pii">PII</option>
          <option value="none">Not PII</option>
        </select>
      </label>
      {downgrade && (
        <label>
          Reason
          <textarea value={reason} onChange={(ev) => setReason(ev.target.value)} aria-describedby="pii-reason-hint" />
          <span id="pii-reason-hint" className="hint">
            At least {catalogAttributes.PII_DOWNGRADE_REASON_MIN} characters; recorded in the audit log.
          </span>
        </label>
      )}
      <Button type="submit" disabled={unchanged || reasonShort || change.isPending}>
        {attribute.piiReviewRequired && piiClass === attribute.piiClass ? 'Confirm classification' : 'Change class'}
      </Button>
      {change.isError && <span role="alert">{errorText(change.error)}</span>}
    </form>
  );
}
